/** Result row labels and PDF cell formatting helpers. */

function slugifyId(s) {
  return String(s || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'node';
}

/** Human-readable source label for a result row. */
export function sourceLabelFromResult(res) {
  if (!res || typeof res !== 'object') return 'Unknown';
  if (res.type === 'phone_mention') {
    return (
      res.source_display_name ||
      res.engine ||
      (typeof res.source === 'string' && res.source) ||
      res.platform ||
      'Search mention'
    );
  }
  const bucket = res.ui_bucket;
  const bucketLabel =
    bucket === 'cyber_trust_score'
      ? 'Domain Security Audit'
      : bucket === 'threat_intelligence'
        ? 'Threat Intelligence (VirusTotal)'
        : bucket === 'wayback_archive'
          ? 'Wayback Machine'
          : bucket === 'email_deliverability'
            ? 'Email Deliverability'
            : bucket === 'registered_service'
              ? 'Registered Service'
              : bucket;
  return (
    res.source_display_name ||
    res.platform ||
    (typeof res.source === 'string' && res.source) ||
    bucketLabel ||
    'Record'
  );
}

/** Title / headline for table row. */
export function titleFromResult(res) {
  if (!res) return '—';
  if (res.source === 'github' && res.details?.username) {
    const u = String(res.details.username).trim();
    if (u) return `GitHub @${u}`;
  }
  if (res.ui_bucket === 'relationship_node') {
    const name = String(res.target_entity || '').trim();
    const rel = String(res.relation_type || '').trim();
    if (name && rel) return `${name} (${rel})`;
    if (name) return name;
    if (rel) return rel;
  }
  if (res.title && String(res.title).trim()) return String(res.title).trim();
  if (res.details?.owner_name) return String(res.details.owner_name);
  if (res.type === 'phone_identity') return 'Phone identity signals';
  if (res.type === 'phone_mention') {
    const t = res.title && String(res.title).trim();
    if (t) return t;
    if (res.url) return String(res.url);
    return 'Web mention';
  }
  if (res.source === 'hibp_status') return res.message || 'HIBP status';
  if (res.source === 'phone_metadata') return 'Carrier & metadata';
  if (res.ui_bucket === 'threat_intelligence') {
    const d = String(res.domain || '').trim();
    const mal = Number(res.malicious) || 0;
    const sus = Number(res.suspicious) || 0;
    const harm = Number(res.harmless) || 0;
    const rl = res.risk_level != null ? String(res.risk_level).trim() : '';
    const head = d ? `Domain: ${d}` : 'VirusTotal domain verdict';
    const stats = `Malicious: ${mal} · Suspicious: ${sus} · Harmless: ${harm}`;
    return rl ? `${head} · ${stats} · VT risk: ${rl}` : `${head} · ${stats}`;
  }
  if (res.ui_bucket === 'cyber_trust_score') {
    const d = String(res.domain || '').trim();
    const rs = res.risk_score;
    const rsStr =
      rs !== undefined && rs !== null && !Number.isNaN(Number(rs)) ? String(Number(rs)) : '—';
    const dmarc = res.dmarc_secure ? 'DMARC: enforced (reject/quarantine)' : 'DMARC: weak or missing';
    const age =
      res.age_days != null && !Number.isNaN(Number(res.age_days))
        ? `${Number(res.age_days)} days`
        : '—';
    const mismatch = res.mismatch_detected ? ' · Corporate/domain mismatch flagged' : '';
    const head = d ? `Domain: ${d}` : 'Domain security audit';
    return `${head} · Infrastructure risk: ${rsStr}/100 · ${dmarc} · Approx. age: ${age}${mismatch}`;
  }
  return '—';
}

export function normalizeUrl(u) {
  if (!u || typeof u !== 'string') return '';
  const t = u.trim();
  if (!t || t === '#') return '';
  return t;
}

function digitsOnly(value) {
  return String(value ?? '').replace(/\D/g, '');
}

const RO_FIXED_PREFIX_MAP = {
  '021': 'Bucuresti',
  '0232': 'Iasi',
  '0264': 'Cluj',
  '0256': 'Timisoara',
  '0268': 'Brasov',
  '0241': 'Constanta',
};

export function inferCarrierFromPrefix(meta, targetRaw) {
  const rawCarrier = String(meta?.details?.carrier || '').trim();
  if (rawCarrier && !/^unknown$/i.test(rawCarrier)) return '';
  const targetDigits = digitsOnly(targetRaw);
  if (!targetDigits) return '';
  const roLocal =
    (targetDigits.startsWith('0') && targetDigits.length === 10 && targetDigits) ||
    (targetDigits.startsWith('40') && targetDigits.length >= 11 ? `0${targetDigits.slice(2)}` : '');
  if (!roLocal) return '';
  const p4 = roLocal.slice(0, 4);
  const p3 = roLocal.slice(0, 3);
  const area = RO_FIXED_PREFIX_MAP[p4] || RO_FIXED_PREFIX_MAP[p3];
  if (!area) return '';
  return `${area} / Fixed-line`;
}

/** Normalize doubled quotes and stray JSON-style quotes in log lines (e.g. Executing Dork / phone variants). */
export function cleanLogText(s) {
  let t = String(s ?? '');
  let prev;
  do {
    prev = t;
    t = t.replace(/""/g, '"');
  } while (t !== prev);
  t = t.replace(/"([\d\s]+)"/g, '$1');
  t = t.replace(/"\s+"/g, ' ');
  if (/^Executing Dork:/i.test(t)) {
    const payload = t.replace(/^Executing Dork:\s*/i, '');
    const parts = payload
      .split(/\s+OR\s+/i)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => x.replace(/^"(.*)"$/, '$1').replace(/\s+/g, ' ').trim());
    t = `Executing Dork: ${parts.join(' | ')}`;
  }
  t = t.replace(/\s{2,}/g, ' ');
  return t.trim();
}

const CATALOG_WEB_URL = 'https://catalog-web.ro';

export function hasSecurityEscalation(list) {
  if (!Array.isArray(list)) return false;
  for (const r of list) {
    if (!r || typeof r !== 'object') continue;
    const tags = Array.isArray(r.matched_tags) ? r.matched_tags.map(String) : [];
    const joined = tags.join(' ').toLowerCase();
    if (joined.includes('spoofing') && joined.includes('suspected')) return true;
    if (joined.includes('fraud reported')) return true;
    if (joined.includes('scam alert')) return true;
    const rs = Number(r.risk_score ?? r.details?.risk_score ?? NaN);
    if (!Number.isNaN(rs) && rs > 90) return true;
  }
  return false;
}

/** Tags shown in PDF Source column (aligned with Phone UI). */
export function extractPdfSourceTags(res) {
  if (!res || typeof res !== 'object') return [];
  const out = [];
  const seen = new Set();
  const raw = Array.isArray(res.matched_tags) ? res.matched_tags : [];
  for (const t of raw) {
    const s = String(t).trim();
    const l = s.toLowerCase();
    if (l.includes('telemarketing suspected') && !seen.has('tm')) {
      out.push('Telemarketing Suspected');
      seen.add('tm');
    }
    if (l.includes('spoofing') && l.includes('suspected') && !seen.has('sp')) {
      out.push('Spoofing Suspected');
      seen.add('sp');
    }
    if (/^whatsapp:/i.test(s) && !seen.has(`wa:${s}`)) {
      out.push(s);
      seen.add(`wa:${s}`);
    }
  }
  if (res.details?.whatsapp_has_account === true && !out.some((x) => /^whatsapp:/i.test(x))) {
    out.push('WhatsApp: Active');
  } else if (
    String(res.details?.whatsapp_probe_state || '').toLowerCase() === 'inconclusive' &&
    !out.some((x) => /^whatsapp:/i.test(x))
  ) {
    out.push('WhatsApp: Inconclusive');
  } else if (
    ['not_registered', 'ineligible'].includes(
      String(res.details?.whatsapp_probe_state || '').toLowerCase(),
    ) &&
    !out.some((x) => /^whatsapp:/i.test(x))
  ) {
    out.push('WhatsApp: Not registered');
  } else if (
    String(res.details?.whatsapp_probe_state || '').toLowerCase() === 'blocked' &&
    !out.some((x) => /^whatsapp:/i.test(x))
  ) {
    out.push('WhatsApp: Blocked');
  }
  return out;
}

/** Source column: one label per line when multiple engines contributed. */
export function formatPdfSourceCell(res, baseSource) {
  const names = [];
  const seen = new Set();
  const push = (raw) => {
    const s = String(raw || '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!s) return;
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(s.charAt(0).toUpperCase() + s.slice(1));
  };

  if (Array.isArray(res?.sources)) {
    res.sources.forEach((s) => push(s));
  }
  const rawSource = res?.source;
  if (typeof rawSource === 'string' && rawSource.trim()) {
    if (/[,+·|]/.test(rawSource) || /\sand\s/i.test(rawSource)) {
      rawSource.split(/[,+·|]|and/i).forEach((part) => push(part));
    } else if (names.length === 0 && !String(res?.source_display_name || baseSource || '').trim()) {
      push(rawSource);
    }
  }
  const display = String(res?.source_display_name || baseSource || '').trim();
  if (display && /[,+·|]/.test(display)) {
    display.split(/[,+·|]/).forEach((part) => push(part));
  } else if (names.length === 0 && display) {
    push(display);
  }
  if (names.length === 0 && baseSource) {
    push(baseSource);
  }
  if (names.length > 1) {
    return names.join('\n');
  }
  return names[0] || String(baseSource || 'Record');
}

/** Prefix title with visible alert markers (PDF + willDrawCell colors). */
export function formatPdfTitleCell(res, baseTitle) {
  const tags = Array.isArray(res?.matched_tags) ? res.matched_tags.map(String) : [];
  const joined = tags.join(' ').toLowerCase();
  const spoof = joined.includes('spoofing') && joined.includes('suspected');
  const fraud = joined.includes('fraud reported');
  const rs = Number(res?.risk_score ?? res?.details?.risk_score ?? NaN);
  const highScam = !Number.isNaN(rs) && rs > 90;
  const telemSuspicious = joined.includes('telemarketing suspected') && rs >= 70 && rs <= 85;
  let prefix = '';
  if (spoof) prefix += '[SPOOFING] ';
  if (fraud || highScam) prefix += '[SCAM ALERT] ';
  if (!prefix && telemSuspicious) prefix += '[SUSPICIOUS] ';
  return prefix + baseTitle;
}

/**
 * Title column: main line + single tag line (8pt via column style; joined with |).
 */
export function formatPdfTitleBlock(res, baseTitle) {
  const main = formatPdfTitleCell(res, baseTitle);
  const rowTags = extractPdfSourceTags(res);
  if (!rowTags.length) return main;
  return `${main}\n${rowTags.join(' | ')}`;
}

/**
 * Catalog Web → fixed URL; also when source/platform is Catalog Web.
 */
export function resolvePdfUrlForRow(res, titleStr, rawUrl) {
  const tit = String(titleStr || '').trim();
  const srcLabel = sourceLabelFromResult(res);
  const plat = String(res?.platform || '');
  const fromSource = /catalog\s*web/i.test(srcLabel) || /catalog\s*web/i.test(plat);
  const catalogTitle = /^catalog\s*web$/i.test(tit);
  if (fromSource) return CATALOG_WEB_URL;
  if (
    catalogTitle &&
    (res?.type === 'phone_identity' || /phone identity signals/i.test(srcLabel))
  ) {
    return CATALOG_WEB_URL;
  }
  const u = normalizeUrl(rawUrl);
  return u || '—';
}

export { slugifyId, digitsOnly };
